import React from "react";
import PropTypes from "prop-types";
import { Empty, Icon, Spin, notification } from "antd";
import SectionBlock from "./components/section-block";
import { SECTION_IDS } from "./constants";
import { fetchCaseSummaryAI, fetchCaseTaskHistory } from "services/case-service";
import { formatCompactAmount } from "./money-format";

/**
 * Credit Decision — mirrors the AI Decision + Final Decision block of the PDF
 * one-pager, in-line on the Credit Checker tab so reviewers don't have to
 * regenerate the PDF just to see the AI verdict.
 *
 * Left card:  AI Decision  — chip (Approve / Refer to Senior / Decline),
 *                            rationale sentence, short_case_summary bullets,
 *                            suggested safe limit.
 * Right card: Final Decision — chip (mirrors the case's live decision on the
 *                              proposed_limit / sanction_limit fields),
 *                              sanction limit + date + credit analyst + senior
 *                              credit name.
 *
 * Only what the case already carries is shown — nothing is fabricated. Empty
 * fields render as "—".
 */

const DECISION_OPTIONS = ["Approve", "Refer to Senior", "Decline"];

// Same semantic map the PDF uses so the chip colour follows the AI/human
// verdict without a lookup table on every render.
function decisionTone(decision) {
    const s = String(decision || "").toLowerCase();
    if (s.includes("approve") && !s.includes("reject") && !s.includes("decline")) {
        return "good";
    }
    if (s.includes("reject") || s.includes("decline")) {
        return "bad";
    }
    if (s.includes("senior") || s.includes("refer") || s.includes("review")) {
        return "watch";
    }
    return "neutral";
}

// Normalise whatever the AI stored into one of the three chip labels the credit
// team uses on the brief. "Senior Credit Review Required" → "Refer to Senior".
function decisionLabel(decision) {
    const s = String(decision || "").toLowerCase();
    if (s.includes("approve")) return "Approve";
    if (s.includes("reject") || s.includes("decline")) return "Decline";
    if (s.includes("senior") || s.includes("refer") || s.includes("review")) {
        return "Refer to Senior";
    }
    return decision || "—";
}

// Render a rupee value the same way the top metrics do: compact "₹XL / XCr"
// when we can, else N.A.
function formatMoney(amount) {
    if (amount === null || amount === undefined || amount === "") return "—";
    const compact = formatCompactAmount(amount);
    if (compact !== null) return `₹${compact}`;
    return `₹${amount}`;
}

function formatDate(raw) {
    if (!raw) return "—";
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return String(raw);
    // dd MMM yyyy — matches the sign-off row on the PDF.
    return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric"
    });
}

// Human-readable label for the stage a reviewer was at. Falls back to the raw
// stage_name when we don't have a short-form.
const STAGE_LABEL_OVERRIDES = {
    "Credit Appraisal": "Credit Analyst",
    "Progcap Credit": "Senior Credit",
    "Progfin Credit": "Senior Credit",
    "Authority Approval": "Credit Head"
};

function designationForRow(row) {
    if (!row) return "—";
    const stageName = row.this_stage && row.this_stage.stage_name;
    if (stageName && STAGE_LABEL_OVERRIDES[stageName]) {
        return STAGE_LABEL_OVERRIDES[stageName];
    }
    if (stageName) return stageName;
    if (row.this_user && row.this_user.designation && row.this_user.designation !== "--") {
        return row.this_user.designation;
    }
    if (row.this_user && row.this_user.groups && row.this_user.groups.length) {
        return row.this_user.groups[0];
    }
    return "—";
}

// Stages the Final Decision card cares about — matches the PDF's reviewer
// trail (only the credit team's sign-off, not sales / bank / KYC / auto-move).
const TRAIL_STAGE_IDS = [7, 8];

// The trail is one row per case_task; keep (user, stage) unique so two people
// touching the same stage (e.g. analyst writes the assessment, senior later
// approves with "ok") each get their own row rather than the latter hiding
// the former.
function collapseTrail(rows) {
    if (!Array.isArray(rows) || !rows.length) return [];
    // Only rows that carry a reviewer we can name AND that sit on the two
    // credit-team stages.
    const usable = rows.filter(r =>
        r
        && r.this_user
        && r.this_user.full_name
        && r.this_stage
        && TRAIL_STAGE_IDS.indexOf(r.this_stage.id) !== -1
    );
    const dedup = [];
    const seen = new Map();
    usable.forEach(r => {
        const key =
            (r.this_user && r.this_user.id) +
            "|" +
            (r.this_stage && r.this_stage.id);
        if (seen.has(key)) {
            // Same user re-touched the same stage — keep the latest remark.
            dedup[seen.get(key)] = r;
            return;
        }
        seen.set(key, dedup.length);
        dedup.push(r);
    });
    return dedup;
}

function trailRemark(row) {
    if (!row) return "";
    // action_remark is what the reviewer typed when they proceeded/referred;
    // remarks is the assignment note. Prefer action_remark, fall back to
    // remarks so we still show something when only the assignment note exists.
    const primary = String(row.action_remark || "").trim();
    if (primary) return primary;
    return String(row.remarks || "").trim();
}

class CreditDecisionSection extends React.Component {
    state = {
        loading: false,
        aiSummary: null,
        error: null,
        refreshing: false,
        trail: [],
        trailLoading: false
    };

    componentDidMount() {
        this.loadSummary(false);
        this.loadTrail();
    }

    componentDidUpdate(prevProps) {
        // Refetch when the case changes.
        if (prevProps.caseId !== this.props.caseId) {
            this.loadSummary(false);
            this.loadTrail();
        }
        // TVR submit asked for an AI refresh — re-run the agent (refresh=true)
        // so the "Refresh AI" button shows its loading/disabled state.
        if (
            prevProps.aiReloadKey !== this.props.aiReloadKey &&
            this.props.aiReloadKey
        ) {
            this.loadSummary(true);
        }
    }

    loadTrail() {
        const { caseId } = this.props;
        if (!caseId) return;
        this.setState({ trailLoading: true });
        fetchCaseTaskHistory(caseId)
            .then(res => {
                const rows = (res && (res.data || res)) || [];
                this.setState({
                    trail: Array.isArray(rows) ? rows : [],
                    trailLoading: false
                });
            })
            .catch(() => {
                // Non-fatal — the card still renders the sanction fields.
                this.setState({ trail: [], trailLoading: false });
            });
    }

    loadSummary(refreshAI) {
        const { caseId } = this.props;
        if (!caseId) return;
        this.setState(
            refreshAI
                ? { refreshing: true, error: null }
                : { loading: true, error: null }
        );
        fetchCaseSummaryAI(caseId, refreshAI)
            .then(res => {
                // /summary-automation/ returns the stored output_json OR (with
                // refresh=true) the freshly re-run output. Both shapes are the
                // same JSON schema the PDF reads.
                const data = (res && (res.data || res)) || null;
                this.setState({
                    loading: false,
                    refreshing: false,
                    aiSummary: data,
                    error: null
                });
                if (refreshAI) {
                    notification.success({
                        message: "AI decision refreshed",
                        description: "Re-ran the underwriting agent for this case."
                    });
                }
            })
            .catch(err => {
                const status = err && err.response && err.response.status;
                this.setState({
                    loading: false,
                    refreshing: false,
                    // 404 = no CASE_SUMMARY analysis stored yet. Show the empty
                    // state instead of an error card so the reviewer can trigger
                    // a first run via the refresh button.
                    error:
                        status === 404
                            ? { notFound: true }
                            : { message: "Could not load AI decision" }
                });
            });
    }

    renderRationaleBullets(summary) {
        const bullets = [];
        const bulletSet = new Set();
        // AI rationale is one sentence + up to 6 short_case_summary bullets.
        // De-dupe on lowercase text so the sentence doesn't repeat the first
        // bullet.
        if (summary && summary.rationale) {
            bullets.push(String(summary.rationale).trim());
            bulletSet.add(String(summary.rationale).trim().toLowerCase());
        }
        (summary && summary.short_case_summary
            ? summary.short_case_summary
            : []
        ).forEach(b => {
            const s = String(b || "").trim();
            if (s && !bulletSet.has(s.toLowerCase())) {
                bullets.push(s);
                bulletSet.add(s.toLowerCase());
            }
        });
        if (!bullets.length) {
            return (
                <div className={"cc-decision__empty"}>
                    No rationale on record yet.
                </div>
            );
        }
        return (
            <ul className={"cc-decision__bullets"}>
                {bullets.map((b, i) => (
                    <li key={i}>{b}</li>
                ))}
            </ul>
        );
    }

    renderChip(activeLabel, tone) {
        const label = decisionLabel(activeLabel);
        return (
            <div className={"cc-decision__chips"}>
                {DECISION_OPTIONS.map(opt => {
                    const active = decisionLabel(activeLabel) === opt;
                    const cls =
                        "cc-decision__chip" +
                        (active ? ` cc-decision__chip--sel cc-decision__chip--${tone}` : "");
                    return (
                        <span key={opt} className={cls}>
                            {opt}
                        </span>
                    );
                })}
            </div>
        );
    }

    renderAiCard() {
        const { loading, error, aiSummary } = this.state;
        if (loading) {
            return (
                <div className={"cc-decision__loading"}>
                    <Spin /> <span>Loading AI decision…</span>
                </div>
            );
        }
        if (error && error.notFound) {
            return (
                <Empty
                    description={
                        "AI decision has not been run for this case yet — click Refresh to trigger it."
                    }
                />
            );
        }
        if (error) {
            return <Empty description={error.message} />;
        }
        if (!aiSummary || aiSummary.not_generated) {
            return (
                <Empty
                    description={
                        "AI decision has not been generated for this case."
                    }
                />
            );
        }
        return (
            <div>
                {this.renderChip(
                    aiSummary.final_decision,
                    decisionTone(aiSummary.final_decision)
                )}
                <div className={"cc-decision__body"}>
                    <div className={"cc-decision__label"}>Rationale</div>
                    {this.renderRationaleBullets(aiSummary)}
                    <div className={"cc-decision__label cc-decision__label--sub"}>
                        Max suggested limit
                    </div>
                    <div className={"cc-decision__value"}>
                        {formatMoney(aiSummary.suggested_safe_limit)}
                    </div>
                </div>
            </div>
        );
    }

    renderTrail() {
        const { trail, trailLoading } = this.state;
        const rows = collapseTrail(trail);
        if (trailLoading && !rows.length) {
            return (
                <div className={"cc-decision__empty"}>
                    Loading reviewer trail…
                </div>
            );
        }
        if (!rows.length) {
            return (
                <div className={"cc-decision__empty"}>
                    No reviewer activity on record yet.
                </div>
            );
        }
        return (
            <ol className={"cc-decision__trail"}>
                {rows.map((r, i) => {
                    const name =
                        (r.this_user && r.this_user.full_name) || "—";
                    const designation = designationForRow(r);
                    const remark = trailRemark(r);
                    const when = r.end_date || r.start_date || r.assignment_date;
                    return (
                        <li key={r.id || i} className={"cc-decision__trail-row"}>
                            <div className={"cc-decision__trail-head"}>
                                <div className={"cc-decision__trail-name"}>
                                    {name}
                                    {r.this_user && r.this_user.level_of_authority ? (
                                        <span className={"cc-decision__trail-loa"}>
                                            {r.this_user.level_of_authority}
                                        </span>
                                    ) : null}
                                </div>
                                <div className={"cc-decision__trail-meta"}>
                                    <span className={"cc-decision__trail-desig"}>
                                        {designation}
                                    </span>
                                    {when ? (
                                        <span className={"cc-decision__trail-date"}>
                                            {formatDate(when)}
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                            {remark ? (
                                <div className={"cc-decision__trail-remark"}>
                                    {remark}
                                </div>
                            ) : (
                                <div
                                    className={
                                        "cc-decision__trail-remark cc-decision__trail-remark--empty"
                                    }
                                >
                                    No remark recorded.
                                </div>
                            )}
                        </li>
                    );
                })}
            </ol>
        );
    }

    renderFinalCard() {
        const {
            humanDecision,
            sanctionLimit,
            sanctionDate,
            conditions
        } = this.props;
        const decision = humanDecision || (sanctionLimit ? "Approve" : null);
        return (
            <div>
                {this.renderChip(decision, decisionTone(decision))}
                <div className={"cc-decision__body"}>
                    {conditions && conditions.length ? (
                        <React.Fragment>
                            <div className={"cc-decision__label"}>Conditions</div>
                            <ul className={"cc-decision__bullets"}>
                                {conditions.map((c, i) => (
                                    <li key={i}>{c}</li>
                                ))}
                            </ul>
                        </React.Fragment>
                    ) : null}
                    <div className={"cc-decision__grid cc-decision__grid--two"}>
                        <div className={"cc-decision__grid-cell"}>
                            <div className={"cc-decision__label"}>Sanction limit</div>
                            <div className={"cc-decision__value"}>
                                {formatMoney(sanctionLimit)}
                            </div>
                        </div>
                        <div className={"cc-decision__grid-cell"}>
                            <div className={"cc-decision__label"}>Sanction date</div>
                            <div className={"cc-decision__value"}>
                                {formatDate(sanctionDate)}
                            </div>
                        </div>
                    </div>
                    <div
                        className={
                            "cc-decision__label cc-decision__label--sub"
                        }
                    >
                        Reviewer trail
                    </div>
                    {this.renderTrail()}
                </div>
            </div>
        );
    }

    // Refresh AI is only actionable at the credit stages — same gate used
    // elsewhere in Credit Checker (TVR reset, bank statement rebuild).
    canRefreshAi = () => [7, 8].includes(Number(this.props.stageId));

    render() {
        const { innerRef, expanded, onToggle } = this.props;
        const { refreshing } = this.state;
        const canRefresh = this.canRefreshAi();
        return (
            <SectionBlock
                id={SECTION_IDS.CREDIT_DECISION}
                innerRef={innerRef}
                title={"Credit Decision"}
                subtitle={"AI verdict and final human sign-off — same as the PDF"}
                icon={"audit"}
                expanded={expanded}
                onToggle={onToggle}
                extra={
                    <button
                        type={"button"}
                        className={"cc-brief-pdf-btn"}
                        onClick={() => this.loadSummary(true)}
                        disabled={refreshing || !canRefresh}
                        title={
                            canRefresh
                                ? "Re-run the underwriting agent"
                                : "AI can only be refreshed at the credit stage"
                        }
                    >
                        <Icon type={refreshing ? "loading" : "sync"} />
                        {refreshing ? "Refreshing…" : "Refresh AI"}
                    </button>
                }
            >
                <div className={"cc-decision"}>
                    <div className={"cc-decision__card"}>
                        <div className={"cc-decision__title"}>AI Decision</div>
                        {this.renderAiCard()}
                    </div>
                    <div className={"cc-decision__card"}>
                        <div className={"cc-decision__title"}>Final Decision</div>
                        {this.renderFinalCard()}
                    </div>
                </div>
            </SectionBlock>
        );
    }
}

CreditDecisionSection.propTypes = {
    innerRef: PropTypes.object,
    caseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    // Gates "Refresh AI" to the credit stages (7, 8).
    stageId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    // Bumped by the parent after a TVR submit re-runs the AI — triggers a reload.
    aiReloadKey: PropTypes.number,
    expanded: PropTypes.bool,
    onToggle: PropTypes.func,
    humanDecision: PropTypes.string,
    sanctionLimit: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    sanctionDate: PropTypes.string,
    conditions: PropTypes.arrayOf(PropTypes.string)
};

export default CreditDecisionSection;
